import { ChargeHistoryPage, ChargeHistoryQuery } from '../../interfaces/stripe/invoice.interface';
import { ErrorHandler, Injectable, OnDestroy, inject } from '@angular/core';
import { ApiClient } from '../http/api-client';
import { TeamsService } from '../teams/teams.service';
import { AppSettings } from '../../app.settings';
import { UiAlertService } from '../ui-alert/ui-alert.service';
import { SessionService } from '../session/session.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { SnackBarComponent } from '../../components/snackbar/snackbar.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Dictionary } from 'highcharts';
import { SpinnerService } from '../spinner/spinner.service';
import { HttpErrorResponse } from '@angular/common/http';

declare var Stripe: any;

const stripe = Stripe(AppSettings.getStripeInfo().key,
    { betas: ['us_bank_account_beta_2'] }
);

const PENDING_ACH_POLL_INTERVAL_MS = 300 * 1000;
// A missed hidden-tab poll only forces a catch-up when the last request is at least this old.
const PENDING_ACH_MISSED_POLL_MIN_GAP_MS = 60 * 1000;
// Cancels a stalled display read so polls never keep joining it (prod p99 is under 2 s).
const PENDING_ACH_REQUEST_TIMEOUT_MS = 30 * 1000;

export enum Plan {
    Free = 'Free',
    Plus = 'Plus',
    PlusAnnual = 'PlusAnnual'
}

@Injectable({
    providedIn: 'root'
})
export class StripeService implements OnDestroy {

    public PREMIUM_MONTHLY_FEE = 7.00;
    public PREMIUM_ANNUAL_FEE = 66.00;
    public STANDARD_CATEGORIES = 6;

    public pendingACHCount = new BehaviorSubject(0);
    private currentWellspace: any;
    private pendingAchRequestId = 0;
    private pendingAchInFlight: { teamId: string; requestId: number; promise: Promise<void> } | null = null;
    private lastPendingAchRequestAt = 0;
    private missedPendingAchPoll = false;
    private readonly errorHandler = inject(ErrorHandler);
    private readonly pendingAchPollTimer: ReturnType<typeof setInterval>;
    private readonly wellspaceSubscription: Subscription;
    private readonly pendingAchVisibilityHandler = (): void => {
        if (document.visibilityState === 'visible' && this.isPendingAchPollDue()) {
            void this.pollPendingAch();
        }
    };

    // private stripe2: any;

    constructor(
        private snackBar: MatSnackBar,
        private restangular: ApiClient,
        private sessionService: SessionService,
        private teamsService: TeamsService,
        private uiAlertService: UiAlertService,
        private spinnerService: SpinnerService
    ) {
        // this.stripe2 = this.setupStripe();
        this.wellspaceSubscription = this.teamsService.wellspaceChangedObservable.subscribe(wellspace => {
            this.currentWellspace = wellspace;
            this.pendingAchRequestId++;
            this.pendingACHCount.next(0);
        });
        this.pendingAchPollTimer = setInterval(() => {
            // Hidden tabs skip the poll; the visibility handler catches up once the tab is shown again.
            if (document.visibilityState === 'hidden') {
                this.missedPendingAchPoll = true;
                return;
            }
            void this.pollPendingAch();
        }, PENDING_ACH_POLL_INTERVAL_MS);
        document.addEventListener('visibilitychange', this.pendingAchVisibilityHandler);
    }

    ngOnDestroy(): void {
        clearInterval(this.pendingAchPollTimer);
        this.wellspaceSubscription.unsubscribe();
        document.removeEventListener('visibilitychange', this.pendingAchVisibilityHandler);
    }

    /*
    private async setupStripe(): Promise<any> {
      return await loadStripe(AppSettings.getStripeInfo().key);
    }
     */

    getProduct(productId: string): Promise<any> {
        return this.restangular.one('stripe').one('product', productId).get().toPromise();
    }

    getCustomerBalance(customerId?: string): Promise<number> {
        if (customerId) {
            return this.getCustomer(customerId).then(customer => {
                return customer.balance;
            });
        }
        if (!this.currentWellspace?.id) {
            return Promise.resolve(0);
        }
        return this.teamsService.getCustomerId(this.currentWellspace).then(newCustomerId => {
            if (!newCustomerId) {
                return 0;
            }
            return this.getCustomer(newCustomerId).then(customer => customer.balance);
        });
    }

    addToCustomerBalance(customerId: string, amount: number): Promise<any> {
        return this.restangular.one('users', customerId).one('stripe', 'addtobalance').customPUT({ amount }).toPromise();
    }

    getCustomerBalanceTransactions(customerId: string): Promise<any> {
        return this.restangular.one('users', customerId).one('stripe', 'balancetransactions').getList().toPromise();
    }

    getBankAccount(clientSecret: string, name: string, email: string): Promise<any> {
        return stripe.collectUsBankAccountForSetup(clientSecret, {
            billing_details: {
                name,
                email,
            },
            metadata: 'YYY',
        }).then((result: any) => {
            return result;
        });
    }

    confirmBankAccountSetup(clientSecret: string): Promise<any> {
        return stripe.confirmUsBankAccountSetup(clientSecret);
    }

    addTeamId(setupIntentId: string, teamId: string): Promise<any> {
        return this.restangular.one('users', this.sessionService.getUserId()).one('stripe').one('banksetupintent').customPUT({
            setupIntentId,
            teamId
        }).toPromise();
    }

    changePlan(team: any, members: Array<any>, planName: string, doneFunc: () => void): void {
        const periodName = planName === Plan.Plus ? 'Monthly' : 'Annual';
        const fee = planName === Plan.Plus ? this.PREMIUM_MONTHLY_FEE : this.PREMIUM_ANNUAL_FEE;
        const chargeAmount = (members.length || 1) * fee;
        const oldChargeAmount = members.length * (team.plan === 'PlusAnnual' ? this.PREMIUM_ANNUAL_FEE : this.PREMIUM_MONTHLY_FEE);
        const oldPeriodName = team.plan === Plan.Plus ? 'Monthly' : 'Annual';
        switch (planName) {
            case Plan.Free:
                const modifyTeam = () => this.teamsService.modifyTeam(team.id, { plan: Plan.Free }).then(() => {
                    this.snackBar.openFromComponent(SnackBarComponent, { data: 'Payment cancelled and plan changed to Free' });
                    team.plan = Plan.Free;
                    doneFunc();
                });
                if (members.length === 0) {
                    modifyTeam();
                    break;
                }
                let moreInfo = team.categories.length > this.STANDARD_CATEGORIES ? `Your ${team.categories.length} current categories will be reduced to ${this.STANDARD_CATEGORIES}.` : '';
                const membersWithCustomAllowanceCount = members.find(member => member.amount !== team.amount)?.length;
                if (membersWithCustomAllowanceCount > 0) {
                    moreInfo += ` Your ${membersWithCustomAllowanceCount} members with a custom allowance will have it reset to the team allowance of $${team.amount}`;
                }
                this.uiAlertService.presentAlertConfirm(`Downgrade to Free Plan for ${team.name}.<br><br>This will cancel the ${oldPeriodName.toLowerCase()} recurring charge of $${oldChargeAmount.toFixed(2)} for the Plus ${oldPeriodName} plan for ${members.length} member${members.length === 1 ? '' : 's'}. ${moreInfo}`)
                    .then(async confirm => {
                        if (confirm) {
                            this.teamsService.getCustomerId(team).then(async customerId => {
                                this.updatePlan(customerId, chargeAmount * members.length, members.length, await this.teamsService.getPaymentMethod(team), team.id, planName).then(() => {
                                    modifyTeam();
                                }).catch((err: HttpErrorResponse) => {
                                    this.spinnerService.hide();
                                    this.snackBar.openFromComponent(SnackBarComponent, { data: 'Unable to update plan.' });
                                });
                            });
                        } else {
                            doneFunc();
                        }
                    });
                break;
            case Plan.Plus:
            case Plan.PlusAnnual:
                const period = planName === Plan.Plus ? 'month' : 'year';
                this.uiAlertService.presentAlertConfirm(
                    `${members.length} member${members.length === 1 ? '' : 's'}.<br><br>Upgrade to Plus ${planName === Plan.PlusAnnual ? 'Annual' : ''} Plan.<br><br>This will initiate ${planName === Plan.Plus ? 'a monthly' : 'an annual'} recurring charge of ` +
                    `$${fee} for each active member on the first day of each ${period}. For the initial ${period}, the amount will be pro-rated and charged immediately. Any available credit balance will be applied first before charging the remaining amount.`
                ).then(async confirm => {
                    if (confirm) {
                        this.spinnerService.show('Changing plan...');
                        this.teamsService.getCustomerId(team).then(async customerId => {
                            this.updatePlan(customerId, chargeAmount * members.length, members.length, await this.teamsService.getPaymentMethod(team), team.id, planName).then(() => {
                                this.teamsService.modifyTeam(team.id, { plan: planName }).then(() => {
                                    this.snackBar.openFromComponent(SnackBarComponent, { data: `Payment made and plan changed to Plus ${periodName}` });
                                    team.plan = planName;
                                    doneFunc();
                                });
                            }).catch((err: HttpErrorResponse) => {
                                this.spinnerService.hide();
                                this.snackBar.openFromComponent(SnackBarComponent, { data: 'Unable to update plan.' });
                            });
                        });
                    } else {
                        doneFunc();
                    }
                });
                break;
        }
    }

    confirmSetup(config: any): Promise<any> {
        return stripe.confirmSetup(config);
    }

    removeCard(paymentMethodId: string): Promise<any> {
        return this.restangular.one('users', paymentMethodId).one('stripe').remove().toPromise();
    }

    getCharges(customerId: string): Promise<any> {
        return this.restangular.one('users', customerId).one('stripe', 'getcharges').getList().toPromise();
    }

    getChargesV2(customerId: string, paymentMethod: string): Promise<any> {
        return this.restangular
            .one('users', customerId)
            .one('stripe', 'paymentMethod')
            .one(paymentMethod, 'charges')
            .getList().toPromise();
    }

    checkIfHasSomePendingAch(teamId: string): Promise<void> {
        const requestId = ++this.pendingAchRequestId;
        this.lastPendingAchRequestAt = Date.now();
        this.missedPendingAchPoll = false;
        const promise = this.restangular.one('teams', teamId).one('pendingcharges').get({ count: true })
            .pipe(timeout(PENDING_ACH_REQUEST_TIMEOUT_MS)).toPromise().then((res: any): void => {
            if (requestId !== this.pendingAchRequestId || this.currentWellspace?.id !== teamId) {
                return;
            }
            if (typeof res?.count !== 'number') {
                throw new Error('Pending ACH response did not contain a numeric count.');
            }
            this.pendingACHCount.next(res.count);
        }).catch((error: unknown): void => {
            // Never rejects: callers fire and forget. Reported to APM; the last displayed count is kept.
            this.errorHandler.handleError(error);
        }).finally(() => {
            if (this.pendingAchInFlight?.promise === promise) {
                this.pendingAchInFlight = null;
            }
        });
        this.pendingAchInFlight = { teamId, requestId, promise };
        return promise;
    }

    /**
     * Always sends a new request, so a read started before a funding action cannot report pre-action state.
     * Skipped when the action's team is not the current wellspace, since that count is not displayed.
     */
    refreshPendingAch(teamId: string): Promise<void> {
        if (!this.currentWellspace || this.currentWellspace.id !== teamId) {
            return Promise.resolve();
        }
        return this.checkIfHasSomePendingAch(teamId);
    }

    private pollPendingAch(): Promise<void> {
        if (!this.currentWellspace) {
            return Promise.resolve();
        }
        const teamId = this.currentWellspace.id;
        const inFlight = this.pendingAchInFlight;
        // Only join a request whose response will still be applied (same team, no wellspace change since).
        return inFlight && inFlight.teamId === teamId && inFlight.requestId === this.pendingAchRequestId
            ? inFlight.promise
            : this.checkIfHasSomePendingAch(teamId);
    }

    private isPendingAchPollDue(): boolean {
        const elapsed = Date.now() - this.lastPendingAchRequestAt;
        return elapsed >= PENDING_ACH_POLL_INTERVAL_MS
            || (this.missedPendingAchPoll && elapsed >= PENDING_ACH_MISSED_POLL_MIN_GAP_MS);
    }

    getInvoices(customerId: string): Promise<any> {
        return this.restangular.one('users', customerId).one('stripe', 'getinvoices').getList().toPromise();
    }

    getTeamInvoicesV2(teamId: string, paymentMethod: string, chargeHistory: boolean): Promise<any> {
        return this.restangular
            .one('teams', teamId)
            .one('stripe')
            .one('paymentMethod', paymentMethod)
            .one('invoices')
            .one(chargeHistory)
            .getList().toPromise();
    }

    getTeamChargeHistoryPage(teamId: string, paymentMethod: string, query: ChargeHistoryQuery): Promise<ChargeHistoryPage> {
        return this.restangular
            .one('teams', teamId)
            .one('stripe')
            .one('paymentMethod', paymentMethod)
            .one('invoices')
            .one(true)
            .get({ ...query, paginated: 'true' }).toPromise();
    }

    getInvoicesForCustomer(customerId: string, chargeHistory: boolean): Promise<any> {
        return this.restangular
            .one('users', customerId)
            .one('stripe')
            .one('invoices', chargeHistory)
            .getList()
            .toPromise();
    }

    getPaymentInfo(userId: string): Promise<any> {
        return this.restangular.one('users', userId).one('stripe', 'getpaymentinfo').get().toPromise();
    }

    getCustomer(customerId: string): Promise<any> {
        return this.restangular.one('users', customerId).one('stripe', 'getcustomer').get().toPromise();
    }

    getElements(paymentMethodTypes?: string[], clientSecret?: string): any {
        const options: any = clientSecret
            ? { clientSecret }
            : {
                mode: 'setup',
                currency: 'usd',
            };
        if (!clientSecret && Array.isArray(paymentMethodTypes) && paymentMethodTypes.length > 0) {
            options.paymentMethodTypes = paymentMethodTypes;
        }
        return stripe.elements(options /*, setupFutureUsage: 'on_session' */);
    }

    decodeDestination(destination: string): string {
        return destination.replace(/:/g, '/');
    }

    makePayment(customerId: string, description: string, amount: number, newMembers: Array<any>, stripePaymentMethod: string, serviceFee: number, teamId: string, planName: string, toTeamId: string, initialFundingAmount?: number): Promise<any> {
        return this.restangular.one('users', stripePaymentMethod).one('stripe').one(`makepayment`).customPOST({
            customerId,
            amount,
            newMembers,
            description,
            serviceFee,
            teamId,
            planName,
            toTeamId,
            initialFundingAmount,
            // 2025-04-29 The proRate value was removed because it is now being accessed directly in the backend
        }).toPromise();
    }

    makePurchasePayments(teamId: string, purchases: any): Promise<any> {
        return this.restangular.one('users').one('stripe').one('makepurchasepayments').customPOST(teamId, purchases).toPromise();
    }

    makeOneTimePayment(
        customerId: string,
        description: string,
        amount: number,
        memberEmails: Array<string>,
        message: string,
        note: string | {
            [key: string]: any
        },
        paymentMethod: string,
        teamId: string, type: string,
        toTeamId: string,
        toWallit: boolean = false,
    ): Promise<any> {
        return this.restangular.one('users', paymentMethod).one('stripe').one('makeonetimepayment').customPOST({
            customerId,
            amount,
            memberEmails,
            message,
            note,
            description,
            teamId,
            type,
            toTeamId,
            toWallit
        }).toPromise();
    }

    updatePlan(customerId: string, amount: number, memberCount: number, stripePaymentMethod: string, teamId: string, planName: string): Promise<any> {
        return this.restangular.one('users', stripePaymentMethod).one('stripe').one('updateplan').customPOST({
            customerId,
            amount,
            memberCount,
            teamId,
            planName
        }).toPromise();
    }

    cancelPayment(customerId: string, stripePaymentMethod: string, teamId: string): Promise<any> {
        return this.restangular.one('users', stripePaymentMethod).one('stripe').one('cancelpayment').customPOST({
            customerId,
            teamId
        }).toPromise();
    }

    changeAllowance(customerId: string, amount: number, allMembers: number, serviceFee: string, teamId: string, paymentMethod: string): Promise<any> {
        return this.restangular.one('users').one('stripe').one('changeallowance').customPOST({
            customerId,
            amount,
            totalMemberCount: allMembers,
            serviceFee,
            teamId,
            paymentMethod
        }).toPromise();
    }

    getSetupIntent(teamId: string, email: string, paymentTargets: Array<string>): Promise<any> {
        return this.restangular.one('users', this.sessionService.getUserId()).one('stripe').one('getsetupintent').get({
            email: encodeURIComponent(email),
            teamId,
            paymentTargets: paymentTargets.join(',')
        }).toPromise();
    }

    async getBankSetupIntent(teamId: string, customerId: string): Promise<any> {
        return this.restangular.one('users', this.sessionService.getUserId()).one('stripe').one('banksetupintent').get({
            teamId,
            customerId: customerId ? customerId : ''
        }).toPromise();
    }

    triggerBillingCycle(customerId: string): Promise<any> {
        return this.restangular.one('users', customerId).one('stripe', 'triggerbillingcycle').put().toPromise();
    }

    triggerSubscriptionBillingCycle(subscriptionId: string): Promise<any> {
        return this.restangular
            .one('stripe')
            .one('subscriptions', subscriptionId)
            .one('triggerbillingcycle')
            .customPUT({})
            .toPromise();
    }

    issueRefund(customerId: string, amount: number): Promise<any> {
        return this.restangular.one('users', customerId).one('stripe', 'issuerefund').customPUT({ amount }).toPromise();
    }

    issueChargeRefund(
        customerId: string,
        chargeId: string,
        amount: number,
        reasonDescription: string,
    ): Promise<any> {
        return this.restangular
            .one('users', customerId)
            .one('stripe')
            .one('charges', chargeId)
            .one('refund')
            .customPUT({ amount, reasonDescription })
            .toPromise();
    }

    searchSubscriptions(brandId: string, fields: Dictionary<any>): Promise<any> {
        for (const [key, value] of Object.entries(fields)) {
            if (!value) {
                delete fields[key];
            }
        }
        return this.restangular.one('stripe').one('search', brandId).get(fields).toPromise();
    }

    private encodeDestination(destination: string): string {
        return destination.replace(/\//g, ':');
    }

}
